import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, IconButton, Switch, Tooltip } from '@mui/material';
import { confirmAlert } from 'react-confirm-alert';
import { BorderColor as BorderColorIcon } from '@mui/icons-material';
import { omit, isEmpty } from 'lodash';
import Bluebird from 'bluebird';
import BaseMUIDataTable from '../../../../components/BaseMUIDataTable/BaseMUIDataTable';
import { getMuiTableDataIndex } from '../../../../helpers/getters';
import { Colors } from '../../../../assets/theme/Colors';
import { fetchSitesIfNeeded, updateSitesFeatureFlags } from '../../../../actions/sites';
import { isUndefinedOrNull } from '../../../../helpers';
import ConfirmationDialog from '../../../../components/ConfirmationDialog';
import AppConfigBulkEditReviewAndSave from '../../../../components/AppConfigBulkEditReviewAndSave';
import useNotificationService from '../../../../hooks/useNotificationService';
import { mapFormToUpdateSitesFeaturesFlags } from '../../../../models/sites';

const customEditToolbar = ({ handleEditClick, editMode, handleReviewClick, reviewButtonEnabled }) => (
  <>
    {!editMode && (
      <Tooltip title="Edit Mode">
        <IconButton onClick={handleEditClick} size="large">
          <BorderColorIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    )}
    {editMode && (
      <Box display="flex" justifyContent="flex-end" flexWrap="no-wrap">
        <Button onClick={handleEditClick}>Exit Edit Mode</Button>
        <Button
          disabled={!reviewButtonEnabled}
          onClick={handleReviewClick}
          style={{ marginLeft: '1rem' }}
          variant="contained"
          color="primary"
        >
          Review and Save
        </Button>
      </Box>
    )}
  </>
);

const ConfirmationDialogComponent = ({ buttons, onClose, message }) => (
  <ConfirmationDialog buttons={buttons} closeDialog={onClose} message={message} />
);

const AdminDashboardAppConfigurationTable = () => {
  const dispatch = useDispatch();
  const sitesEntities = useSelector((state) => state.entities.sites);
  const sites = Object.values(sitesEntities.byId);
  const featureFlagsEntities = useSelector((state) => state.entities.featureFlags);
  const featureFlags = Object.values(featureFlagsEntities.byId);
  const [editMode, setEditMode] = useState(false);
  const [editedSites, setEditedSites] = useState([]);
  const [inReview, setInReview] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { addSuccessNotification, addErrorNotification } = useNotificationService();

  useEffect(() => {
    dispatch(fetchSitesIfNeeded());
  }, []);

  const scrollTop = () => window.scrollTo({ left: 0, top: 0, behavior: 'smooth' });

  const handleSaveClick = (data) => {
    const updatedData = data.map((d) => d.newData);
    setIsLoading(true);
    return Bluebird.try(() => dispatch(updateSitesFeatureFlags(mapFormToUpdateSitesFeaturesFlags(updatedData))))
      .then(() => {
        setInReview(false);
        setEditMode(false);
        setEditedSites([]);
        setIsLoading(false);
        addSuccessNotification('Site(s) updated.');
        scrollTop();
      })
      .catch((error) => {
        setIsLoading(false);
        addErrorNotification(error, error.message);
        scrollTop();
      });
  };

  const handleReviewClick = () => setInReview(true);

  const handleEditClick = () => {
    if (editedSites.length > 0) {
      return confirmAlert({
        message: 'Are you sure you want to exit without saving?',
        buttons: [
          {
            label: 'No',
            color: 'primary',
          },
          {
            label: 'Yes',
            color: 'primary',
            onClick: () => {
              setEditMode(!editMode);
              setEditedSites([]);
            },
          },
        ],
        customUI: ConfirmationDialogComponent,
      });
    }

    setEditMode(!editMode);
    return setEditedSites([]);
  };

  const nameCustomBodyRender = (value, tableMeta, csv = false) => {
    const site = sites[getMuiTableDataIndex(tableMeta)];

    if (csv) {
      return site.name;
    }

    return (
      <>
        {site.name}

        {site && site.disabled_at !== null && (
          <>
            <br />

            <span style={{ color: Colors.sites.disabled.color, fontWeight: 'bold' }}>Disabled</span>
          </>
        )}
      </>
    );
  };

  const typeCustomBodyRender = (value, tableMeta, csv = false) => {
    const site = sites[getMuiTableDataIndex(tableMeta)];

    if (csv) {
      return site.type;
    }

    return site.type.charAt(0).toUpperCase() + site.type.slice(1); // first letter to uppercase
  };

  const disabledAtCustomBodyRender = (value) => (isUndefinedOrNull(value) ? 'Yes' : 'No');

  const featureFlagCustomBodyRender = (value, tableMeta, ff = {}, csv = false) => {
    const site = sites[getMuiTableDataIndex(tableMeta)];
    const siteHasFlagActive = (site?.feature_flags || []).find((flag) => flag === ff.id);

    if (editMode) {
      const siteEdited = editedSites.find((s) => s.site_id === site.id);
      const ffAlreadyEdited = siteEdited?.featureFlags?.[ff.id];

      return (
        <Switch
          color="secondary"
          onChange={(event) => {
            // Add new item if site was not edited
            if (!siteEdited) {
              return setEditedSites([
                ...editedSites,
                {
                  site_id: site.id,
                  featureFlags: { [ff.id]: { checked: event.target.checked, id: ff.id, name: ff.name } },
                },
              ]);
            }

            // Remove ff from item and remove item if there are no ff entries for it
            if (siteEdited && ffAlreadyEdited) {
              const editedSitesFiltered = editedSites
                .map((es) =>
                  es.site_id !== siteEdited.site_id
                    ? es
                    : {
                        ...es,
                        featureFlags: omit(es.featureFlags, ff.id),
                      }
                )
                .filter((es) => !isEmpty(es.featureFlags));

              return setEditedSites(editedSitesFiltered);
            }

            // Add ff to the exisiting item
            return setEditedSites(
              editedSites.map((es) =>
                es.site_id !== siteEdited.site_id
                  ? es
                  : {
                      ...es,
                      featureFlags: {
                        ...es.featureFlags,
                        [ff.id]: { checked: event.target.checked, id: ff.id, name: ff.name },
                      },
                    }
              )
            );
          }}
          defaultChecked={!!siteHasFlagActive}
          sx={
            siteEdited && siteEdited.featureFlags[ff.id]
              ? {
                  '& .MuiSwitch-switchBase': {
                    color: Colors.red,
                    '& + .MuiSwitch-track': {
                      opacity: 0.6,
                      backgroundColor: Colors.red,
                    },
                    '&.Mui-checked': {
                      color: Colors.green.main,
                      '& + .MuiSwitch-track': {
                        opacity: 0.6,
                        backgroundColor: Colors.green.main,
                      },
                    },
                  },
                }
              : {}
          }
        />
      );
    }

    if (csv) {
      return siteHasFlagActive ? 'On' : 'Off';
    }

    return (
      <span style={{ color: siteHasFlagActive ? Colors.green.main : Colors.red }}>
        {siteHasFlagActive ? 'On' : 'Off'}
      </span>
    );
  };

  return (
    <>
      {inReview && (
        <AppConfigBulkEditReviewAndSave
          onExit={() => setInReview(false)}
          onSave={handleSaveClick}
          dialogOpen={inReview}
          editesSites={editedSites}
          sites={sites}
          isLoading={isLoading}
        />
      )}

      <BaseMUIDataTable
        tableId="#admin-dashboard/app-config"
        title="App Configuration"
        isLoading={sitesEntities.inflight || featureFlagsEntities.inflight}
        data={sites}
        columns={[
          {
            name: 'id',
            label: 'Site Id',
            options: {
              display: true,
              filter: false,
            },
          },
          {
            name: 'name',
            label: 'Site Name',
            options: {
              display: true,
              filter: false,
              customBodyRender: (value, tableMeta) => nameCustomBodyRender(value, tableMeta),
              customBodyRenderCSV: (value, tableMeta) => nameCustomBodyRender(value, tableMeta, true),
            },
          },
          {
            name: 'type',
            label: 'Site Type',
            options: {
              display: true,
              customFilterListOptions: {
                render: (value) => `Type: ${value}`,
              },
              customBodyRender: (value, tableMeta) => typeCustomBodyRender(value, tableMeta),
              customBodyRenderCSV: (value, tableMeta) => typeCustomBodyRender(value, tableMeta, true),
            },
          },
          {
            name: 'disabled_at',
            label: 'Active',
            options: {
              display: true,
              filter: true,
              filterList: ['Yes '],
              customFilterListOptions: {
                render: (value) => `Active: ${value}`,
              },
              customBodyRender: (value) => disabledAtCustomBodyRender(value),
              customBodyRenderCSV: (value) => disabledAtCustomBodyRender(value),
            },
          },
          ...featureFlags.map((ff) => ({
            name: ff.name,
            label: ff.name,
            options: {
              display: true,
              filter: true,
              customFilterListOptions: {
                render: (value) => `${ff.name}: ${value}`,
              },
              filterOptions: {
                names: ['Flag Inactive', 'Flag Active'],
                logic: (option, filters, row) => {
                  if (!filters || filters.length === 0) {
                    return false;
                  }

                  const site = sites.find((s) => s.id === row[0]);
                  const siteHasFlagActive = (site?.feature_flags || []).find((flag) => flag === ff.id);

                  if (!siteHasFlagActive) {
                    return !filters.includes('Flag Inactive');
                  }

                  if (siteHasFlagActive) {
                    return !filters.includes('Flag Active');
                  }

                  return true;
                },
              },
              customBodyRender: (value, tableMeta) => featureFlagCustomBodyRender(value, tableMeta, ff),
              customBodyRenderCSV: (value, tableMeta) => featureFlagCustomBodyRender(value, tableMeta, ff, true),
            },
          })),
        ]}
        options={{
          responsive: 'simple',
          selectableRows: 'none',
          setRowProps: (row, dataIndex) => {
            const site = sites[dataIndex];

            if (!site || site.disabled_at === null) {
              return false;
            }

            return {
              sx: { borderLeft: `8px solid ${Colors.sites.disabled.color}` },
            };
          },
          customToolbar: () =>
            customEditToolbar({
              handleEditClick,
              editMode,
              handleReviewClick,
              reviewButtonEnabled: editedSites.length,
            }),
        }}
      />
    </>
  );
};

export default AdminDashboardAppConfigurationTable;
